import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Card,
  CardContent,
  Grid,
  Box,
  IconButton,
  Collapse,
  Chip,
  Menu,
  MenuItem,
  styled,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { WsButton, WsColors, WsTypoColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import { DeleteIconButton } from '../components/StyledIconButtons';
import { IBusinessData, IStageData } from '../customizeStageStatus/helper';

import { jobOptions } from './BusinessStageStatusPopUp';

interface IStatusTypographyProps {
  color: string;
}

const bgcolorStatusMap = new Map();

bgcolorStatusMap.set('JobOption', WsColors.TheBlues200);

const colorStatusMap = new Map();
colorStatusMap.set('JobOption', WsColors.TheBlues600);

const StatusTypography = styled(Typography)(({ color }: IStatusTypographyProps) => ({
  backgroundColor: bgcolorStatusMap.get(color),
  color: colorStatusMap.get(color),
  padding: 4,
  width: 'fit-content',
  borderRadius: 4,
}));

interface IBusinessDefinitionCardProps {
  businessDefinition: IBusinessData;
  cardIndex: number;
  onEdit: number | null;
  onSubmit: (isArchive: boolean) => void;
  setOnEdit: (index: number) => void;
  handleEditClick: (index: number, isArchive: boolean) => void;
  handleDeleteClick: (index: number, isArchive: boolean) => void;
  deleteStage: (cardIndex: number, subIndex: number) => void;
  deleteStatus: (cardIndex: number, subIndex: number, superSubIndex: number) => void;
  handleToggle: (index: string) => void;
  openCardIndex: string | null;
  setStageData: (data: IStageData) => void;
  setBusinessData: (data: IBusinessData) => void;
  setOpenBusiness: (data: boolean) => void;
  setOpenSubStatus: (value: boolean) => void;
  setOpenStatusModal: (value: boolean) => void;
  setBusinessName: (name: string) => void;
  setJobOption: (jobOption: string) => void;
  handleArchive: (index: number, isArchive: boolean) => void;
  handleRename: (index: number) => void;
  onArchive: boolean;
  total: number;
}

const BusinessDefinitionCard = ({
  businessDefinition,
  cardIndex,
  onEdit,
  onSubmit,
  setOnEdit,
  handleEditClick,
  handleDeleteClick,
  deleteStage,
  deleteStatus,
  handleToggle,
  openCardIndex,
  setStageData,
  setBusinessData,
  setOpenBusiness,
  setOpenSubStatus,
  setOpenStatusModal,
  setBusinessName,
  setJobOption,
  handleArchive,
  handleRename,
  onArchive,
  total,
}: IBusinessDefinitionCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => setAnchorEl(null);

  return (
    <Grid item key={cardIndex}>
      <Card
        sx={{
          maxWidth: 330,
          minWidth: 330,
          minHeight: 450,
          maxHeight: 450,
          backgroundColor: WsColors.BorderGrey,
          mx: 2,
          overflowY: 'auto',
        }}
      >
        <CardContent>
          <Grid
            container
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid
              item
              ml={0.5}
              xs={businessDefinition.id == undefined || onEdit === cardIndex ? 8.5 : 6}
            >
              <Box
                sx={{
                  py: 1,
                  borderRadius: 1,
                }}
              >
                <WsTypography variant={WsTypoVariants.TITLE}>
                  {businessDefinition?.businessDefinitionName}
                </WsTypography>
              </Box>
            </Grid>
            {!(businessDefinition.id == undefined || onEdit === cardIndex) && (
              <Grid item>
                <StatusTypography color={'JobOption'}>
                  {jobOptions.find((option) => option.value === businessDefinition.jobOption)
                    ?.label ?? 'All Jobs'}
                </StatusTypography>
              </Grid>
            )}
            {businessDefinition.id == undefined || onEdit === cardIndex ? (
              <Grid item xs>
                <WsButton
                  onClick={() => onSubmit(businessDefinition.isArchive ?? false)}
                  type="button"
                  size="medium"
                >
                  Save
                </WsButton>
              </Grid>
            ) : (
              <Grid item>
                <IconButton size="small" onClick={handleOpenMenu}>
                  <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                  {onArchive && (
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        handleEditClick(cardIndex, businessDefinition.isArchive ?? false);
                      }}
                    >
                      Edit
                    </MenuItem>
                  )}
                  {onArchive && (
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        setOpenBusiness(true);
                        handleRename(cardIndex);
                      }}
                    >
                      Rename
                    </MenuItem>
                  )}
                  {onArchive && (
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        setOpenBusiness(true);
                        handleRename(cardIndex);
                      }}
                    >
                      Change Scope
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      handleArchive(cardIndex, !businessDefinition.isArchive);
                    }}
                  >
                    {businessDefinition.isArchive ? 'Unarchive' : 'Archive'}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      handleDeleteClick(cardIndex, businessDefinition.isArchive ?? false);
                    }}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              </Grid>
            )}
          </Grid>
          {onArchive && (onEdit === cardIndex || total == 0) && (
            <Grid container justifyContent="center" mt={2}>
              <Grid item>
                <Box
                  sx={{
                    backgroundColor: WsColors.White,
                    boxShadow: 1,
                    pr: 10,
                    pl: 10,
                    borderRadius: 1,
                  }}
                >
                  <Grid container alignItems="baseline" p={1} justifyContent="center">
                    <Grid item>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setOnEdit(cardIndex);
                          setBusinessData(businessDefinition);
                          setOpenStatusModal(true);
                          setBusinessName(businessDefinition.businessDefinitionName);
                          setJobOption(businessDefinition?.jobOption ?? '');
                        }}
                      >
                        <Grid container justifyContent="center" alignContent="center">
                          <Grid item xs={12}>
                            <AddCircleOutlineIcon
                              sx={{ color: WsColors.TheBlues500, width: 30, height: 30 }}
                            />
                          </Grid>
                          <Grid item>
                            <WsTypography
                              bold
                              variant={WsTypoVariants.TITLE}
                              color={WsTypoColors.PRIMARY}
                            >
                              Add a Stage
                            </WsTypography>
                          </Grid>
                        </Grid>
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
          {businessDefinition?.stageStatusData.map((stage, subIndex) => (
            <Card sx={{ minHeight: 60, backgroundColor: WsColors.White, my: 1 }} key={subIndex}>
              <Grid container spacing={2} alignItems="center" justifyContent="flex-start" p={1}>
                <Grid container alignItems="center" justifyContent="flex-start" p={2}>
                  <Grid
                    item
                    xs={businessDefinition.id == undefined || onEdit === cardIndex ? 7 : 9}
                  >
                    <WsTypography bold variant={WsTypoVariants.TITLE}>
                      {stage.stage}
                    </WsTypography>
                  </Grid>
                  {(businessDefinition.id == undefined || onEdit === cardIndex) && (
                    <Grid item ml={3.5}>
                      <DeleteIconButton onClick={() => deleteStage(cardIndex, subIndex)} />
                    </Grid>
                  )}
                  <Grid
                    item
                    ml={businessDefinition.id == undefined || onEdit === cardIndex ? 0 : 3.5}
                  >
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => handleToggle(`toggle_${businessDefinition.id}_${subIndex}`)}
                    >
                      {openCardIndex === `toggle_${businessDefinition.id}_${subIndex}` ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </Grid>
                </Grid>
                <Collapse in={openCardIndex === `toggle_${businessDefinition.id}_${subIndex}`}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      {stage.selectedStatuses.map((status, superSubIndex) => (
                        <Grid item key={superSubIndex}>
                          {businessDefinition.id == undefined || onEdit === cardIndex ? (
                            <Chip
                              color="default"
                              sx={{
                                backgroundColor: WsColors.TheBlues100,
                                fontWeight: 'bold',
                                borderColor: WsColors.TheBlues100,
                                height: '10',
                                '& .MuiChip-label': {
                                  display: 'block',
                                  whiteSpace: 'normal',
                                },
                              }}
                              label={status}
                              variant="outlined"
                              onDelete={() => deleteStatus(cardIndex, subIndex, superSubIndex)}
                              deleteIcon={
                                businessDefinition.id == undefined || onEdit === cardIndex ? (
                                  <ClearIcon />
                                ) : undefined
                              }
                            />
                          ) : (
                            <Chip
                              color="default"
                              sx={{
                                backgroundColor: WsColors.TheBlues100,
                                fontWeight: 'bold',
                                borderColor: WsColors.TheBlues100,
                                height: '10',
                                '& .MuiChip-label': {
                                  display: 'block',
                                  whiteSpace: 'normal',
                                },
                              }}
                              label={status}
                              variant="outlined"
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                    {(businessDefinition.id == undefined || onEdit === cardIndex) && (
                      <Grid container justifyContent="flex-start" pt={2}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setOnEdit(cardIndex);
                            setStageData(businessDefinition?.stageStatusData[subIndex]);
                            setBusinessData(businessDefinition);
                            setOpenSubStatus(true);
                            setJobOption(businessDefinition?.jobOption ?? '');
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <AddIcon sx={{ color: WsColors.TheBlues500, width: 20, height: 20 }} />
                          <WsTypography
                            bold
                            variant={WsTypoVariants.TITLE}
                            color={WsTypoColors.PRIMARY}
                            cursor="pointer"
                          >
                            Add Status
                          </WsTypography>
                        </IconButton>
                      </Grid>
                    )}
                  </CardContent>
                </Collapse>
              </Grid>
            </Card>
          ))}
        </CardContent>
      </Card>
    </Grid>
  );
};
export default BusinessDefinitionCard;
